import { Result, ResultProps, Spin, Typography } from "antd";
import { FC, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { clearPreAuthenticationPath, getPreAuthenticationPath, persistPreAuthenticationPathIfNoneIsSet } from "../../utils/oidcUtils";
import { centerContainerStyle } from "../styles";

interface OidcStateProp {
  heightOverride?: string;
  icon: OidcStateIcon;
  state: string;
}

type OidcStateIcon = ResultProps["status"] | "loading";
const OidcState: FC<OidcStateProp> = ({ state, icon, heightOverride }) => {
  useTranslation();

  useEffect(() => {
    console.log("Updated OIDC state", icon, state);
  }, [icon, state]);

  const wrapperStyle = useMemo(
    () => ({
      ...centerContainerStyle,
      ...heightOverride ? { height: heightOverride } : {},
    }),
    [heightOverride],
  );

  const iconEl = useMemo(
    () => icon === "loading" ? <Spin size="large"/> : undefined,
    [icon],
  );
  const status = useMemo(
    () => icon !== "loading" ? icon : undefined,
    [icon],
  );

  return (
    <div style={wrapperStyle}>
      <Result
        icon={iconEl}
        status={status}
        title={(
          <Typography.Title level={2}>
            <Trans ns="common" i18nKey={`oidc.oidcState.${state}.title`}/>
          </Typography.Title>
        )}
        extra={(
          <Typography.Text>
            <Trans ns="common" i18nKey={`oidc.oidcState.${state}.message`}/>
          </Typography.Text>
        )}
      />
    </div>
  );
};

export const OidcAuthenticating: FC = () => {
  persistPreAuthenticationPathIfNoneIsSet();
  return (
    <OidcState icon="loading" state="authenticating"/>
  );
};

export const OidcAuthenticationError: FC = () => {
  clearPreAuthenticationPath();
  return (
    <OidcState icon="error" state="authenticationError"/>
  );
};

export const OidcCallbackSuccess: FC = () => (
  <OidcState icon="loading" state="callbackSuccess"/>
);

export const OidcLoading: FC = () => (
  <OidcState icon="loading" state="loading"/>
);

export const OidcLoggingOut: FC = () => (
  <OidcState icon="loading" state="loggingOut"/>
);

export const OidcSessionLost: FC = () => (
  <OidcState icon="loading" state="sessionLost"/>
);

export const OidcWaiting: FC = () => {
  const preAuthenticationPath = getPreAuthenticationPath();
  const navigate = useNavigate();
  useEffect(
    () => {
      if (preAuthenticationPath != null) {
        navigate(preAuthenticationPath);
        clearPreAuthenticationPath();
      }
    },
    [navigate, preAuthenticationPath],
  );

  return (
    <OidcState icon="loading" state="waiting"/>
  );
};

export const Unauthorized: FC = () => (
  <OidcState icon="error" state="unauthorized" heightOverride="100%"/>
);
